import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { ArrowLeft, BookOpen, FileText, Brain, Loader2, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth, getAuthHeaders } from "../App";
import DashboardHeader from "../components/DashboardHeader";

const ChapterSelect = () => {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedChapters, setSelectedChapters] = useState([]);

  useEffect(() => {
    fetchBook();
  }, [bookId]);

  const fetchBook = async () => {
    try {
      const response = await axios.get(`${API}/books/${bookId}`, {
        headers: getAuthHeaders(token)
      });
      setBook(response.data);
    } catch (error) {
      toast.error("Failed to load book");
      navigate("/books");
    } finally {
      setLoading(false);
    }
  };

  const toggleChapter = (chapterId) => {
    setSelectedChapters(prev => 
      prev.includes(chapterId)
        ? prev.filter(id => id !== chapterId)
        : [...prev, chapterId]
    );
  };

  const selectAll = () => {
    if (selectedChapters.length === book?.chapters?.length) {
      setSelectedChapters([]);
    } else {
      setSelectedChapters(book?.chapters?.map(ch => ch.chapter_id) || []);
    }
  };

  const handleCreateTest = () => {
    if (selectedChapters.length === 0) {
      toast.error("Please select at least one chapter");
      return;
    }
    navigate(`/books/${bookId}/test`, { state: { selectedChapters } });
  };

  const handleCreateQuiz = () => {
    if (selectedChapters.length === 0) {
      toast.error("Please select at least one chapter");
      return;
    }
    navigate(`/books/${bookId}/quiz`, { state: { selectedChapters } });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <DashboardHeader />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#4F46E5]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="chapter-select-page">
      <DashboardHeader />

      <main className="container-main py-8">
        <Button 
          variant="ghost" 
          className="mb-6 gap-2"
          onClick={() => navigate("/books")}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Books
        </Button>

        <div className="max-w-3xl mx-auto">
          {/* Book Info */}
          <Card className="card-base mb-6">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-indigo-100 to-indigo-50 rounded-xl flex items-center justify-center">
                <BookOpen className="w-7 h-7 text-[#4F46E5]" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {book?.title}
                </h1>
                <p className="text-slate-500">
                  {book?.chapters?.length || 0} chapters • {book?.content_type}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Chapter Selection */}
          <Card className="card-base mb-6">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Select Chapters</CardTitle>
                  <CardDescription>
                    Choose which chapters to include in your test or quiz
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={selectAll}
                  data-testid="select-all-btn"
                >
                  {selectedChapters.length === book?.chapters?.length ? "Deselect All" : "Select All"}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {book?.chapters?.length > 0 ? (
                book.chapters.map((chapter, index) => (
                  <div
                    key={chapter.chapter_id}
                    className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${
                      selectedChapters.includes(chapter.chapter_id)
                        ? 'border-[#4F46E5] bg-indigo-50'
                        : 'border-slate-100 hover:border-slate-200'
                    }`}
                    onClick={() => toggleChapter(chapter.chapter_id)}
                    data-testid={`chapter-${chapter.chapter_id}`}
                  >
                    <div className="flex items-start gap-4">
                      <Checkbox
                        checked={selectedChapters.includes(chapter.chapter_id)}
                        onCheckedChange={() => toggleChapter(chapter.chapter_id)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium text-slate-400">
                            Chapter {index + 1}
                          </span>
                          {selectedChapters.includes(chapter.chapter_id) && (
                            <CheckCircle className="w-4 h-4 text-[#4F46E5]" />
                          )}
                        </div>
                        <h3 className="font-semibold text-slate-900 mb-1">{chapter.title}</h3>
                        <p className="text-sm text-slate-500 mb-2">{chapter.summary}</p>
                        {chapter.key_topics?.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {chapter.key_topics.slice(0, 4).map((topic, i) => (
                              <span 
                                key={i}
                                className="px-2 py-0.5 bg-slate-100 rounded-full text-xs text-slate-600"
                              >
                                {topic}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <p className="text-slate-500">No chapters found. The content may still be processing.</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="space-y-4">
            {/* Primary Actions - Test and Quiz */}
            <div className="flex gap-4">
              <Button
                onClick={handleCreateTest}
                disabled={selectedChapters.length === 0}
                className="flex-1 btn-primary h-14 text-lg gap-2"
                data-testid="create-test-btn"
              >
                <FileText className="w-5 h-5" />
                Create Test
              </Button>
              <Button
                onClick={handleCreateQuiz}
                disabled={selectedChapters.length === 0}
                className="flex-1 btn-accent h-14 text-lg gap-2"
                data-testid="create-quiz-btn"
              >
                <Brain className="w-5 h-5" />
                Create Quiz
              </Button>
            </div>
            
            {/* Lecture Notes Button */}
            <Button
              onClick={() => navigate(`/books/${bookId}/lecture-notes?chapters=${selectedChapters.join(',')}`)}
              disabled={selectedChapters.length === 0}
              variant="outline"
              className="w-full h-12 gap-2 border-2 border-dashed border-slate-300 hover:border-[#4F46E5] hover:bg-indigo-50"
              data-testid="create-notes-btn"
            >
              <BookOpen className="w-5 h-5" />
              Generate Lecture Notes
              <span className="text-xs text-slate-400 ml-2">(5 credits)</span>
            </Button>
          </div>

          {selectedChapters.length > 0 && (
            <p className="text-center text-sm text-slate-500 mt-4">
              {selectedChapters.length} chapter{selectedChapters.length > 1 ? 's' : ''} selected
            </p>
          )}
        </div>
      </main>
    </div>
  );
};

export default ChapterSelect;
